import { useId } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/**
 * The component vocabulary. docs/design-dashboard.md §7 is the reasoning.
 *
 * **The set is closed.** A new button variant is a design decision, not a class
 * name — it goes in that document first. The reason is not tidiness: an
 * interface where every screen invented its own controls is one where nobody
 * can predict what a control does under stress, and this one is read by people
 * whose hands are shaking.
 *
 * Three rules run through everything below:
 *
 * - **Hierarchy from weight and spacing before colour.** If a heading needs a
 *   colour to read as a heading, it is the wrong size.
 * - **Colour is never the only carrier of state.** Every status has a text
 *   label and a shape. Render the screen greyscale and it must still be
 *   readable — a WCAG 2.2 AA requirement, and AA is a procurement gate.
 * - **No container without a job.** A card that only draws a border around
 *   content already grouped by spacing gets deleted.
 */

/* ------------------------------------------------------------------ surfaces */

/**
 * The glass surface primitive (Stage 1 glass foundation, 2026-09-03).
 *
 * The Find My sheet, in one place. Home's content column, its notification
 * stack, the sign-in card and the live view's count and map panels were each
 * re-declaring the same three things inline — the `.sheet` material, the large
 * radius, and the deep soft shadow the freeze wrote as a raw rgba. This is that
 * lockup, named once, so the whole app reads as one pane of glass and a
 * follow-on page cannot drift the shadow by a pixel.
 *
 * It is **glass over the map only** (`design-dashboard.md` §8b): a table read
 * at a desk has nothing behind it to refract, so it stays on paper. Two
 * elevations — `high` is the standing sheet lifted clear of the map, `medium`
 * is a card floating just above it (a notification). The rim + elevation are
 * composed in the class, never inline, so they cannot fight each other.
 */
export function GlassPanel({
  children,
  elevation = 'high',
  className = '',
  style,
  role,
  'aria-label': ariaLabel,
}: {
  children: ReactNode;
  elevation?: 'high' | 'medium';
  className?: string;
  style?: CSSProperties;
  role?: string;
  'aria-label'?: string;
}) {
  const elev = elevation === 'high' ? 'elev-3' : 'elev-2';
  return (
    <div
      role={role}
      aria-label={ariaLabel}
      style={style}
      className={`sheet ${elev} rounded-[var(--radius-3)] border border-[var(--glass-edge)] ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * A card, for a handful of *dissimilar* things — and never for rows that share
 * a schema. That is the line §7.1 draws and the most common dashboard mistake
 * the bible names: a card grid where a table belongs triples the height and
 * kills column scanning. A roster is a `Table`; a single site's summary tile or
 * a plan-and-billing box is a `Card`.
 *
 * On paper with a hairline and the resting elevation — off the map, so no
 * glass. Optional header with a title and one action, the same shape as
 * `Section` but bounded rather than open.
 */
export function Card({
  title,
  action,
  children,
  className = '',
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`elev-1 rounded-[var(--radius-2)] border border-[var(--line)] bg-[var(--paper)] p-4 ${className}`}
    >
      {title ? (
        <div className="mb-3 flex items-baseline justify-between gap-4">
          <h3 className="text-[15px] leading-5 font-[650]">{title}</h3>
          {action}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/**
 * A row in a list of *dissimilar* items — a place, a device, a plan feature —
 * where a table would be the wrong instrument because the rows do not share
 * columns. A hairline divider and space, leading content on the left and an
 * action or status on the right; the whole row is a link target when
 * `onClick` is given (and only then does it carry hover and a chevron, the D26
 * rule: a chevron promises somewhere to go).
 *
 * Still not the answer for schema rows — a roster, a sweep board, a delivery
 * ledger are `Table`s (§7.1). This is for the short, mixed lists a table would
 * make look like a spreadsheet of one-offs.
 */
export function ListRow({
  children,
  trailing,
  onClick,
  label,
}: {
  children: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  /** Distinguishes this row's action from the identical one above it (§7 Button label). */
  label?: string;
}) {
  const inner = (
    <>
      <span className="min-w-0 flex-1 text-[14px]">{children}</span>
      {trailing ? (
        <span className="shrink-0 text-[14px] text-[var(--ink-2)]">{trailing}</span>
      ) : null}
      {onClick ? (
        <span aria-hidden className="shrink-0 text-[var(--ink-3)]">
          &rsaquo;
        </span>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="flex w-full items-center gap-3 border-b border-[var(--line)] py-3 text-left last:border-0 hover:bg-[var(--paper-3)]"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-3 border-b border-[var(--line)] py-3 last:border-0">
      {inner}
    </div>
  );
}

/**
 * A segmented control — the pill of two-to-four mutually exclusive choices
 * Apple uses for a view switch (Find My's list/map, the mobile home's
 * You/Site). Text labels only, never icons (§7.3, WCAG), and it announces as a
 * radio group so a screen reader hears "2 of 3, selected".
 *
 * The active segment lifts onto paper with the resting elevation; the track is
 * `--paper-3`. For a scoped switch (filtering a list, choosing a tab that is
 * really a segment) — not for navigation, which is the rail.
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex gap-0.5 rounded-[var(--radius-2)] bg-[var(--paper-3)] p-0.5"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={`h-8 rounded-[var(--radius-1)] px-3 text-[13px] font-medium whitespace-nowrap transition-colors ${
              active
                ? 'elev-1 bg-[var(--paper)] text-[var(--ink)]'
                : 'text-[var(--ink-2)] hover:text-[var(--ink)]'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------- text */

export function PageTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <header className="mb-6">
      <h1 className="text-[25px] leading-[31px] font-[650] tracking-[-0.02em]">{children}</h1>
      {sub ? <p className="mt-1 text-[14px] text-[var(--ink-2)]">{sub}</p> : null}
    </header>
  );
}

/**
 * A section is a hairline and space, not a card. Sections separated by cards is
 * the layout tell that most reliably marks an interface nobody art-directed.
 */
export function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-6">
      <div className="mb-2 flex items-baseline justify-between gap-4 border-b border-[var(--line)] pb-2">
        <h2 className="text-[17px] leading-6 font-[650]">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Uppercase, tracked, quiet. The instrument register's label. */
export function Label({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] leading-[14px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase">
      {children}
    </span>
  );
}

/** Machine values only — identifiers, hashes, CAP fields. Never prose. */
export function Mono({ children }: { children: ReactNode }) {
  return (
    <span className="font-[var(--font-mono)] text-[13px] leading-[18px] break-all">{children}</span>
  );
}

/* ----------------------------------------------------------------- controls */

/**
 * Three kinds and no more. No gradients, no shadows, and **no icon-only
 * buttons** — an icon-only control in a safety tool is one somebody misuses
 * under stress, and it fails WCAG 2.2 AA's name and target-size requirements.
 *
 * `size="live"` is 64px, for the one control that alerts a building.
 */
export function Button({
  children,
  onClick,
  variant = 'secondary',
  size = 'default',
  disabled,
  type = 'button',
  danger,
  full,
  label,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'quiet';
  size?: 'default' | 'live' | 'auth';
  disabled?: boolean;
  type?: 'button' | 'submit';
  danger?: boolean;
  full?: boolean;
  /**
   * An accessible name that *distinguishes* this control from others with the
   * same visible text.
   *
   * Every "Remove" in a table reads as the same button to a screen reader, and
   * a row action that cannot be told from the one above it is a row action
   * somebody triggers on the wrong person. Having a label is not enough — it
   * has to identify what it acts on.
   */
  label?: string;
}) {
  const base =
    'inline-flex items-center justify-center gap-2 border font-medium transition-colors disabled:cursor-not-allowed';

  const sizing = {
    // 36px, matching the table row. A 40px control inside a 36px row grew
    // the row to fit and silently undid the density pass — the kind of
    // thing only visible when a component set is checked against itself.
    default: 'h-9 px-3 text-[14px] min-w-[44px] rounded-[var(--radius-1)] disabled:opacity-45',
    live: 'h-16 px-8 text-[17px] min-w-[220px] rounded-[var(--radius-1)] disabled:opacity-45',
    // The auth register (`design-dashboard.md` §7b): 44px capsule at 17px,
    // transcribed from Apple's own `.button`. A capsule appears nowhere else
    // in the dashboard on purpose — it marks the surface that holds no data.
    // A full-width primary is the largest thing on a page with nothing else on
    // it, so the dashboard's 45% disabled state leaves a grey slab dominating
    // the screen while doing nothing. It has to recede here, not just dim.
    auth: 'h-11 px-6 text-[17px] tracking-[-0.374px] rounded-full disabled:opacity-25',
  }[size];

  const kind = {
    primary: danger
      ? 'border-[var(--alert)] bg-[var(--alert)] text-white hover:opacity-90'
      : 'border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)] hover:opacity-90',
    secondary:
      'border-[var(--line-2)] bg-[var(--paper)] text-[var(--ink)] hover:bg-[var(--paper-3)]',
    quiet: 'border-transparent bg-transparent text-[var(--ink-2)] hover:text-[var(--ink)]',
  }[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`${base} ${sizing} ${kind} ${full ? 'w-full' : ''}`}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  autoFocus,
  inputMode,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
  autoFocus?: boolean;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block">
        <Label>{label}</Label>
      </span>
      <input
        type={type}
        value={value}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--focus)]"
      />
      {/* Helper text says something the label does not. Restating the label is
          on the slop list. */}
      {hint ? <span className="mt-1 block text-[13px] text-[var(--ink-2)]">{hint}</span> : null}
    </label>
  );
}

export function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block">
        <Label>{label}</Label>
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--focus)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------------------- status */

export type Tone = 'neutral' | 'safe' | 'attention' | 'alert';

/**
 * A text label and a dot. Never a coloured pill on its own.
 *
 * The dot is a redundant carrier, not the message: strip the colour and the
 * word still says what the state is. That is the greyscale test, and it is the
 * one an assessor will actually run.
 */
export function Status({ tone, children }: { tone: Tone; children: ReactNode }) {
  const colour = {
    neutral: 'var(--ink-3)',
    safe: 'var(--safe)',
    attention: 'var(--attention)',
    alert: 'var(--alert)',
  }[tone];

  return (
    <span className="inline-flex items-center gap-2 text-[14px]">
      <span
        aria-hidden
        className="inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ background: colour }}
      />
      {children}
    </span>
  );
}

/**
 * A number that matters: large, tabular, with its label beneath. Not inside a
 * bordered card — the size is the emphasis.
 */
export function Metric({
  label,
  value,
  tone = 'neutral',
  sub,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  sub?: ReactNode;
}) {
  const colour = {
    neutral: 'var(--ink)',
    safe: 'var(--safe-text)',
    attention: 'var(--attention)',
    alert: 'var(--alert)',
  }[tone];

  return (
    <div>
      <div className="tabular text-[25px] leading-[31px] font-[650]" style={{ color: colour }}>
        {value}
      </div>
      <div className="mt-1">
        <Label>{label}</Label>
      </div>
      {sub ? <div className="mt-0.5 text-[13px] text-[var(--ink-2)]">{sub}</div> : null}
    </div>
  );
}

export function Metrics({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-x-8 gap-y-4">{children}</div>;
}

/* -------------------------------------------------------------------- lists */

/**
 * Tables are the default for a list of things that share a schema.
 *
 * **Not cards.** A card grid for rows sharing a schema triples the vertical
 * space, breaks column scanning and makes sorting impossible — the most common
 * and most obvious mistake in dashboards. Cards are for a handful of dissimilar
 * things; a roster and a sweep board are tables.
 */
export function Table({
  head,
  children,
  stickyHeader,
}: {
  head: ReactNode[];
  children: ReactNode;
  /**
   * Pin the column-label row to the top of the scroll while the body scrolls
   * beneath it, so a long list never loses its headings. Inside a {@link
   * Directory} it pins *under* the sticky screen header via the
   * `--dir-header-h` offset that Directory publishes; standalone it pins to the
   * top of the nearest scroll container. Off by default — a short table gains
   * nothing and a boxed table already scrolls its own way.
   */
  stickyHeader?: boolean;
}) {
  return (
    // A non-sticky table wraps in an `overflow-x-auto` scroller so wide content
    // scrolls itself and never pushes the page sideways. A `stickyHeader` table
    // uses `overflow-x-clip` instead, and the distinction is load-bearing:
    // `overflow-x: auto` upgrades `overflow-y` from visible to auto (the CSS
    // visible→auto rule) and so makes this wrapper its own scroll container —
    // the sticky <thead> then sticks to *this box* not the page, and
    // `top: var(--dir-header-h)` shoves the header down into the table, below the
    // first row (the bug). `overflow-x: clip` does NOT establish a scroll
    // container and leaves `overflow-y` at visible, so the header pins against
    // the page under the directory band — AND it still contains a too-wide row
    // (e.g. a long email) so the *page* never scrolls sideways (verified: with a
    // 40-char email at 520px, document.scrollWidth === clientWidth). The trade
    // is that an over-wide sticky table clips rather than h-scrolls; cells that
    // can grow long carry a `title` so the value stays reachable.
    <div className={stickyHeader ? 'overflow-x-clip' : 'overflow-x-auto'}>
      <table className="w-full border-collapse text-[14px]">
        <thead
          className={stickyHeader ? 'sticky z-10' : undefined}
          style={stickyHeader ? { top: 'var(--dir-header-h, 0px)' } : undefined}
        >
          <tr className="border-b border-[var(--line)]">
            {head.map((h, i) => (
              <th
                key={i}
                scope="col"
                className={`px-2 py-2 text-left text-[11px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase first:pl-0 last:pr-0 ${
                  // The pinned column header wears the OPAQUE, theme-aware
                  // `--glass-solid` fill (`#ffffff` light / `#111a2e` dark) so it
                  // fully occludes the rows scrolling beneath it — owner call
                  // 2026-09-15, having weighed the trade: the list body is glass
                  // over the map, so an opaque strip reads as a faint box against
                  // it, but the translucent `--sheet` alternative let dark row text
                  // ghost straight through the pinned strip (verified in-browser).
                  // Box over ghost, and the token adapts to both themes.
                  stickyHeader ? 'bg-[var(--glass-solid)]' : ''
                }`}
              >
                {/* An action column passes '' — visually blank is right, but a
                    blank <th> makes a screen reader announce nothing for every
                    cell under it (axe: empty-table-header). */}
                {h === '' ? <span className="sr-only">Actions</span> : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Row({ children, active }: { children: ReactNode; active?: boolean }) {
  // `active` marks the row whose inspect overlay is open, so the focus anchor is
  // the row plus the frosted panel — not a grey rectangle. Token-based, so it
  // reads in both themes; `aria-current` names it for assistive tech.
  return (
    <tr
      aria-current={active ? 'true' : undefined}
      className={`h-9 border-b border-[var(--line)] last:border-0 ${
        active ? 'bg-[var(--paper-3)]' : ''
      }`}
    >
      {children}
    </tr>
  );
}

export function Cell({
  children,
  numeric,
  wide,
}: {
  children: ReactNode;
  numeric?: boolean;
  wide?: boolean;
}) {
  return (
    <td
      className={`px-2 align-middle first:pl-0 last:pr-0 ${numeric ? 'tabular text-right' : ''} ${
        wide ? 'w-full' : 'whitespace-nowrap'
      }`}
    >
      {children}
    </td>
  );
}

/**
 * An empty state says what to do next in one sentence and offers the action.
 * Never an illustration.
 */
export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-3 border border-dashed border-[var(--line-2)] px-4 py-6 text-[14px] text-[var(--ink-2)]">
      <p className="m-0 max-w-prose">{children}</p>
      {action}
    </div>
  );
}

/**
 * A list that failed to load — and the way to try again.
 *
 * Deliberately, visibly **not** an `Empty`. The global query client runs
 * `retry: 1` and does not `throwOnError`, so a failed list query sits as
 * `isError` with `data === undefined`. Collapse that into `?? []` and a screen
 * says "No teams yet" when the truth is "we could not reach the server" — the
 * confident false state this product exists to avoid, and the exact bug class
 * the repo documents. So a failed load gets its own affordance: a solid alert
 * bar in full ink (not the dashed, muted `Empty`), the honest sentence, and a
 * Retry that calls the query's own `refetch`. `role="alert"` so assistive tech
 * hears it, because the person who most needs to know the count is wrong is the
 * one who cannot see the colour.
 */
export function LoadError({
  children = 'Something went wrong loading this. It may be a network hiccup, not an empty list.',
  onRetry,
}: {
  children?: ReactNode;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 border-l-[3px] border-[var(--alert)] bg-[var(--paper-3)] px-4 py-4 text-[14px] text-[var(--ink)]"
    >
      <p className="m-0 max-w-prose">{children}</p>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/**
 * A finding worth reading, not a decoration. `attention` is the default
 * because these are almost always something that needs doing.
 */
export function Finding({ children, tone = 'attention' }: { children: ReactNode; tone?: Tone }) {
  const colour = {
    neutral: 'var(--line-strong)',
    safe: 'var(--safe)',
    attention: 'var(--attention)',
    alert: 'var(--alert)',
  }[tone];

  return (
    <div
      className="mb-2 border-l-[3px] bg-[var(--paper-3)] px-3 py-2 text-[14px]"
      style={{ borderColor: colour }}
    >
      {children}
    </div>
  );
}

/**
 * Sentence-case a string the API returns in lower case.
 *
 * The readiness gaps and the event states are written to read inside a
 * sentence ("…because no drill has ever been completed here"), and this screen
 * uses them as standalone lines. Capitalising here rather than changing the API
 * keeps one wording shared by the dashboard, the app and the report — three
 * surfaces inventing their own phrasing is how a product ends up describing the
 * same gap three different ways.
 */
export function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function Spinner({ children = 'Loading' }: { children?: ReactNode }) {
  // A hairline and a quiet word. A skeleton screen more elaborate than the
  // content it stands in for is on the slop list.
  return <p className="py-6 text-[14px] text-[var(--ink-2)]">{children}…</p>;
}

/**
 * A tall field with a label that floats — the auth register only
 * (`design-dashboard.md` §7b).
 *
 * Geometry transcribed from Apple's own `.form-textbox`, measured rather than
 * approximated: 56px tall, 12px radius, 17/21 text at −0.374px tracking, and a
 * translucent white ground that is designed to sit on grey rather than white.
 * The label resting at 17px and shrinking to 12px is what makes a 56px field
 * legible without a separate label line above it.
 *
 * Deliberately not the dashboard's `Field`. That one is 36px because it sits in
 * a table among rows being scanned; this one sits alone on a page with no data
 * on it, is reached on a phone as often as a desk, and its labels have to
 * survive being read at arm's length.
 */
export function AuthField({
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  autoComplete,
  autoFocus,
  maxLength,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
  autoComplete?: string;
  autoFocus?: boolean;
  maxLength?: number;
  hint?: string;
}) {
  // The hint says something the label does not ("it expires in ten minutes"),
  // so it has to reach a screen reader too. Sighted users get it from
  // proximity; everyone else needs the association.
  const hintId = useId();
  return (
    <label className="block">
      <span className="relative block h-14">
        <input
          type={type}
          value={value}
          inputMode={inputMode}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          maxLength={maxLength}
          aria-describedby={hint ? hintId : undefined}
          onChange={(e) => onChange(e.target.value)}
          /* A space, not an empty string: `:placeholder-shown` is what tells
             the label whether the field is empty, and it never matches when
             there is no placeholder at all. */
          placeholder=" "
          className="peer h-14 w-full rounded-[12px] border border-[var(--line-2)] bg-[var(--paper)]/80 px-4 pt-[18px] text-[17px] leading-[21px] tracking-[-0.374px] text-[var(--ink)] outline-none placeholder:text-transparent focus:border-[var(--focus)] focus:ring-1 focus:ring-[var(--focus)]"
        />
        <span className="pointer-events-none absolute top-[10px] left-4 text-[12px] leading-[16px] tracking-[-0.12px] text-[var(--ink-3)] transition-all duration-125 ease-in peer-placeholder-shown:top-[18px] peer-placeholder-shown:text-[17px] peer-placeholder-shown:leading-[21px] peer-placeholder-shown:tracking-[-0.374px] peer-focus:top-[10px] peer-focus:text-[12px] peer-focus:leading-[16px] peer-focus:tracking-[-0.12px]">
          {label}
        </span>
      </span>
      {hint ? (
        <span
          id={hintId}
          className="mt-2 block px-1 text-[13px] leading-[18px] text-[var(--ink-2)]"
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}

/**
 * A standing notice across the top of every screen.
 *
 * Taken from Apple Business Manager, which puts *"Verify your organisation to
 * unlock Apple Business features. Verify Now ›"* above every page until it is
 * resolved — one line, a light ground, an information glyph, and the remedy as
 * an inline link rather than a button.
 *
 * The rule it encodes is the part worth copying: **a condition that blocks the
 * product does not live on a settings page.** It follows the person around
 * until it is fixed, and it carries the way to fix it.
 *
 * Reserved for exactly that — a state where something the product exists to do
 * cannot be done. Not for tips, not for announcements, not for anything a
 * person can safely ignore, because a banner that is ever ignorable is a banner
 * that is always ignored.
 */
export function Banner({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="border-b border-[var(--line)] bg-[var(--paper-3)] px-6 py-2.5">
      <p className="mx-auto flex max-w-[1100px] flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-[14px] leading-[19px] text-[var(--ink)]">
        <span aria-hidden className="text-[var(--ink-3)]">
          &#9432;
        </span>
        {children}
        {action}
      </p>
    </div>
  );
}

/**
 * The remedy inside a `Banner` or a table cell: a link with a chevron, never a
 * button.
 *
 * Apple Business Manager puts *"Verify Now ›"* both in the standing banner and
 * inside the Status cell of the locations table — the same control in both
 * places, because it is the same act. Putting the fix where the problem is
 * stated is the pattern; a button would claim more weight than the sentence
 * around it.
 */
export function InlineAction({
  children,
  onClick,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  /**
   * An accessible name that *distinguishes* this control from others with the
   * same visible text — every "Remove" in a list of team members reads as the
   * same control to a screen reader otherwise, and a remove that cannot be told
   * from the one above it is one triggered on the wrong person or place (§7,
   * the same reasoning as `Button`'s `label`).
   */
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      // A standalone remedy/link control, so it carries its own ≥24px target
      // (WCAG 2.5.8) rather than relying on being inline in a line of text.
      className="inline-flex min-h-[24px] items-center gap-0.5 text-[14px] text-[var(--accent)] underline-offset-2 hover:underline"
    >
      {children}
      <span aria-hidden>&rsaquo;</span>
    </button>
  );
}
